import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { JButton } from "@/components/JButton";
import { toast } from "sonner";
import { z } from "zod";
import { SIGNUP_COUNTRIES } from "@/lib/countries";

export const Route = createFileRoute("/login")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; context?: "admin" } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    context: search.context === "admin" ? ("admin" as const) : undefined,
  }),

  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/login" },
      { title: "Sign in to Jozori | جذوري" },
      { name: "description", content: "Sign in or create your Jozori parent account." },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/login" }],
  }),
  component: LoginPage,
});

const emailSchema = z.string().trim().email("Enter a valid email").max(255);
const pwSchema = z.string().min(8, "Password must be at least 8 characters").max(72);

function LoginPage() {
  const { user, loading, signIn, signUp, signInWithGoogle, signInWithApple } = useAuth();
  const navigate = useNavigate();
  const { redirect, context } = Route.useSearch();
  const isAdminContext = context === "admin";
  const explicitRedirect =
    redirect?.startsWith("/") && !redirect.startsWith("//") ? redirect : undefined;
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", [
          "super_admin",
          "admin",
          "content_manager",
          "moderator",
          "support",
          "finance",
        ]);
      if (cancelled) return;
      const isStaff = (data?.length ?? 0) > 0;
      const target =
        explicitRedirect ?? (isStaff || isAdminContext ? "/app/admin" : "/app");
      navigate({ to: target as never, replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, loading, navigate, explicitRedirect, isAdminContext]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const eRes = emailSchema.safeParse(email);
    if (!eRes.success) return toast.error(eRes.error.issues[0].message);
    const pRes = pwSchema.safeParse(password);
    if (!pRes.success) return toast.error(pRes.error.issues[0].message);
    setBusy(true);
    if (mode === "signup") {
      if (!country) {
        setBusy(false);
        return toast.error("Please select your country");
      }
      const { error } = await signUp(email, password, name.trim() || "Parent", country);
      if (error) toast.error(error);
      else {
        toast.success("Welcome! Check your inbox to verify your email if prompted.");
        // Fire-and-forget welcome email (won't block signup if it fails)
        import("@/lib/email/send").then(({ sendTransactionalEmail }) =>
          sendTransactionalEmail({
            templateName: "welcome",
            recipientEmail: email,
            idempotencyKey: `welcome-${email}`,
            templateData: { name: name.trim() || undefined },
          }).catch(() => {})
        );
      }
    } else {
      const { error } = await signIn(email, password);
      if (error) toast.error(error);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="px-5 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2">
          <span className="font-display-ar text-2xl font-black text-primary">جذوري</span>
          <span className="font-display-en text-lg font-extrabold text-foreground/40">/</span>
          <span className="font-display-en text-lg font-extrabold tracking-tight">JOZORI</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md bg-card border-2 border-clay/30 rounded-3xl shadow-2xl p-7 md:p-9">
          <h1 className="font-display-en text-3xl font-bold mb-1 tracking-tight">
            {isAdminContext
              ? "Staff sign in"
              : mode === "signin"
              ? "Welcome back"
              : "Create your account"}
          </h1>
          <p className="text-sm text-foreground/60 mb-6">
            {isAdminContext
              ? "Sign in with your staff account to continue to the admin area."
              : mode === "signin"
              ? "Pick up where your family left off."
              : "Start your child's bilingual journey."}
          </p>

          {isAdminContext && (
            <div className="mb-5 rounded-2xl border-2 border-primary/40 bg-primary/5 px-4 py-3 text-sm">
              <p className="font-bold text-primary mb-0.5">Admin access required</p>
              <p className="text-foreground/70 text-xs">
                You'll be returned to the admin page after signing in.
              </p>
            </div>
          )}

          <button
            onClick={async () => {
              setBusy(true);
              const { error } = await signInWithGoogle((explicitRedirect ?? "/app"));
              if (error) toast.error(error);
              setBusy(false);
            }}
            disabled={busy}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-2xl border-2 border-clay/40 hover:bg-clay/15 font-semibold transition-colors mb-4 disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l6.2 5.2C40.9 35.3 44 30 44 24c0-1.2-.1-2.3-.4-3.5z" />
            </svg>
            Continue with Google
          </button>

          <button
            onClick={async () => {
              setBusy(true);
              const { error } = await signInWithApple((explicitRedirect ?? "/app"));
              if (error) toast.error(error);
              setBusy(false);
            }}
            disabled={busy}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-2xl bg-black text-white hover:bg-black/90 font-semibold transition-colors mb-4 disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            Continue with Apple
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-clay/40" />
            <span className="text-xs text-foreground/40 uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-clay/40" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
              />
            )}
            {mode === "signup" && (
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
              >
                <option value="" disabled>
                  Country / البلد
                </option>
                {SIGNUP_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.en} — {c.ar}
                  </option>
                ))}
              </select>
            )}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={255}
              className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              maxLength={72}
              className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
            />
            <JButton type="submit" disabled={busy} className="w-full" size="lg">
              {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
            </JButton>
            {mode === "signin" && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-primary font-semibold hover:underline">
                  Forgot password?
                </Link>
              </div>
            )}
          </form>

          <p className="text-sm text-center mt-5 text-foreground/60">
            {mode === "signin" ? "New to Jozori?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="text-primary font-bold hover:underline"
            >
              {mode === "signin" ? "Create account" : "Sign in"}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
